/**
 * @file categories.ts
 *
 * Category API for the shop admin, wired to the real commerce-engine `/v1` catalog
 * API. Thin functions over {@link v1Get} / {@link v1Mutate} that map the `/v1`
 * {@link CategoryResponseV1} to the design-reviewed {@link ShopCategory} the screens
 * consume, and the {@link ShopCategoryPayload} back to the `/v1` write body via
 * {@link buildCategoryDto}. This layer is the only place that knows backend specifics
 * (field names, `parentId` null = root, the soft `DELETE`), so the store and screens
 * stay untouched. {@link toShopCategory} and {@link fetchProductCategories} live here
 * (the products tab's form/filter pickers import them) so the mapping has one home.
 */

import { v1Get, v1Mutate } from './v1-request';
import type { ShopCategory, ShopCategoryPayload } from './shop-types';
import type { CategoryResponseV1, CreateCategoryRequestV1 } from './v1-types';

/** Default page size; the list fetches all categories (the tree is built client-side). */
export const CATEGORIES_PER_PAGE = 100;

/** Matches a canonical UUID; guards `imageId` so a mock-library numeric id degrades to none. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Maps a `/v1` category to the {@link ShopCategory} the screens consume. `count` is the
 * `/v1` `productCount` (non-deleted products of any status), defaulting to `0` when the
 * response omits it (e.g. the tree); a root category's `parentId` (`null`) maps to the
 * `""` sentinel.
 *
 * @param c - The `/v1` category resource.
 *
 * @returns The category in the design-reviewed `Shop*` shape.
 */
export function toShopCategory(c: CategoryResponseV1): ShopCategory {
  return {
    id: c.id,
    name: c.name,
    slug: c.slug,
    parent: c.parentId ?? '',
    description: c.description ?? '',
    count: c.productCount ?? 0,
    image: c.image ? { id: c.image.id, src: c.image.url } : null,
  };
}

/**
 * Builds the `/v1` category write body from the form's payload. Backs both create
 * (`POST`) and update (`PATCH`): a blank slug is omitted (the engine derives it),
 * `description`/`parentId`/`imageId` are sent as `null` to clear, the `""` parent
 * sentinel maps to `null` (top-level), and a non-UUID image id (a mock-library pick)
 * degrades to no image rather than failing the write.
 *
 * @param payload - The category write payload from the form.
 *
 * @returns The `/v1` category request body.
 */
export function buildCategoryDto(payload: ShopCategoryPayload): CreateCategoryRequestV1 {
  const imageId = payload.image?.id;
  const dto: CreateCategoryRequestV1 = {
    name: payload.name,
    description: payload.description || null,
    parentId: payload.parent || null,
    imageId: imageId && UUID_RE.test(imageId) ? imageId : null,
  };
  if (payload.slug) {
    dto.slug = payload.slug;
  }
  return dto;
}

/**
 * Fetches the full category list (up to {@link CATEGORIES_PER_PAGE}) for the list
 * view and the parent-category select; the tree is built client-side from the flat list.
 *
 * @returns All categories, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchCategories(): Promise<ShopCategory[]> {
  const res = await v1Get<CategoryResponseV1[]>(`/categories?pageSize=${CATEGORIES_PER_PAGE}`);
  return res.data.map(toShopCategory);
}

/**
 * Fetches the full category list for the product form pickers and list filter
 * dropdown. Same call as {@link fetchCategories}; kept as a named export so the
 * products store's import doesn't need to know they share an endpoint.
 *
 * @returns All categories, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails.
 */
export { fetchCategories as fetchProductCategories };

/**
 * Fetches a single category by id, used to seed the edit form on a direct link or
 * refresh (the list view passes its cached copy when present).
 *
 * @param id - The category id to fetch.
 *
 * @returns The category.
 *
 * @throws {WcApiError} When the request fails (e.g. 404 for an unknown id).
 */
export async function fetchCategoryById(id: string): Promise<ShopCategory> {
  const res = await v1Get<CategoryResponseV1>(`/categories/${id}`);
  return toShopCategory(res.data);
}

/**
 * Creates a new category against the `/v1` API.
 *
 * @param payload - The category write payload.
 *
 * @returns The created category, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails (e.g. a `category_slug_conflict`).
 */
export async function createCategory(
  payload: ShopCategoryPayload,
): Promise<ShopCategory> {
  const created = await v1Mutate<CategoryResponseV1>(
    '/categories',
    'POST',
    buildCategoryDto(payload),
  );
  return toShopCategory(created);
}

/**
 * Updates an existing category against the `/v1` API.
 *
 * @param id - The category id to update.
 * @param payload - The category write payload.
 *
 * @returns The updated category, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails (e.g. a `category_slug_conflict`).
 */
export async function updateCategory(
  id: string,
  payload: ShopCategoryPayload,
): Promise<ShopCategory> {
  const updated = await v1Mutate<CategoryResponseV1>(
    `/categories/${id}`,
    'PATCH',
    buildCategoryDto(payload),
  );
  return toShopCategory(updated);
}

/**
 * Soft-deletes a category (`DELETE /v1/categories/:id`, returning 204).
 *
 * @param id - The category id to delete.
 *
 * @throws {WcApiError} When the request fails (e.g. `category_has_children`).
 */
export async function deleteCategory(id: string): Promise<void> {
  await v1Mutate(`/categories/${id}`, 'DELETE');
}
